import { supabase } from "./supabaseClient";

export async function dbCreateGame(userId) {
  const { data, error } = await supabase
    .from("games")
    .insert({ user_id: userId, total_score: 0, rounds_played: 0, is_complete: false })
    .select("id")
    .single();
  if (error) { console.error("createGame:", error); return null; }
  return data.id;
}

export async function dbSaveRound(gameId, userId, roundNumber, actualYear, guessYear, score) {
  const { error } = await supabase.from("rounds").insert({
    game_id: gameId,
    user_id: userId,
    round_number: roundNumber,
    actual_year: actualYear,
    guess_year: guessYear,
    score,
  });
  if (error) console.error("saveRound:", error);
}

export async function dbUpdateGame(gameId, totalScore, roundsPlayed, isComplete) {
  const { error } = await supabase.from("games")
    .update({
      total_score: totalScore,
      rounds_played: roundsPlayed,
      is_complete: isComplete,
      ...(isComplete ? { completed_at: new Date().toISOString() } : {}),
    })
    .eq("id", gameId);
  if (error) console.error("updateGame:", error);
}

export async function dbFetchHistory(userId) {
  const { data, error } = await supabase
    .from("games")
    .select(`id, started_at, completed_at, total_score, rounds_played, is_complete,
             rounds(round_number, actual_year, guess_year, score)`)
    .eq("user_id", userId)
    .eq("is_complete", true)
    .order("completed_at", { ascending: false })
    .limit(50);
  if (error) { console.error("fetchHistory:", error); return []; }
  return data;
}

export async function dbFetchLeaderboard() {
  const { data, error } = await supabase
    .from("leaderboard")
    .select("*")
    .limit(50);
  if (error) { console.error("fetchLeaderboard:", error); return []; }
  return data;
}

export async function dbFetchProfileSummary(userId) {
  const { data, error } = await supabase
    .from("leaderboard")
    .select("*")
    .eq("user_id", userId)
    .single();
  if (error) {
    console.error("fetchProfileSummary:", error);
    return null;
  }
  return data;
}

export async function dbFetchUserProfile(userId) {
  const { data, error } = await supabase
    .from("public_profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error || !data) return null;

  return {
    about:               data.about,
    favoritePeriodStart: data.favorite_period_start,
    favoritePeriodEnd:   data.favorite_period_end,
    favoriteRulerKey:    data.favorite_ruler,
    favoriteRulerYear:   data.favorite_ruler_year ?? 1000,
    favoriteRegionName:  data.favorite_region,
    favoriteRegionYear:  data.favorite_region_year ?? 1000,
  };
}
export async function dbUpsertUserProfile(profile) {
  const row = {
    user_id:               profile.user_id,
    about:                 profile.about,
    favorite_period_start: profile.favoritePeriodStart,
    favorite_period_end:   profile.favoritePeriodEnd,
    favorite_ruler:        profile.favoriteRulerKey,
    favorite_ruler_year:   profile.favoriteRulerYear,
    favorite_region:       profile.favoriteRegionName,
    favorite_region_year:  profile.favoriteRegionYear,
  };

  const { data, error } = await supabase
    .from("public_profiles")
    .upsert(row, { onConflict: "user_id" })
    .select()
    .single();

  if (error) {
    console.error("Upsert error:", error.message, error.details, error.hint);
    return null;
  }
  return data;
}

export async function dbFetchProfileRounds(userId) {
  const { data, error } = await supabase
    .from("rounds")
    .select("actual_year, score")
    .eq("user_id", userId)
    .order("actual_year", { ascending: true });
  if (error) {
    console.error("fetchProfileRounds:", error);
    return [];
  }
  return data;
}

export async function dbFetchRoundsByGameIds(gameIds) {
  if (!gameIds || gameIds.length === 0) return [];
  const { data, error } = await supabase
    .from("rounds")
    .select("game_id, round_number, actual_year, guess_year, score")
    .in("game_id", gameIds)
    .order("game_id", { ascending: true })
    .order("round_number", { ascending: true });
  if (error) {
    console.error("fetchRoundsByGameIds:", error);
    return [];
  }
  return data || [];
}

export async function dbFetchProfileHistory(userId, page = 1, pageSize = 5) {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const { data, count, error } = await supabase
    .from("games")
    .select(`id, started_at, completed_at, total_score, rounds_played, is_complete,
             rounds(round_number, actual_year, guess_year, score)`, { count: "exact" })
    .eq("user_id", userId)
    .eq("is_complete", true)
    .order("completed_at", { ascending: false })
    .range(from, to);
  if (error) {
    console.error("fetchProfileHistory:", error);
    return { data: [], count: 0 };
  }
  return { data: data || [], count: count || 0 };
}

export async function dbFetchProfileGames(userId, limit = 100) {
  const { data, error } = await supabase
    .from("games")
    .select(`id, started_at, completed_at, total_score`)
    .eq("user_id", userId)
    .eq("is_complete", true)
    .order("completed_at", { ascending: true })
    .range(0, limit - 1);
  if (error) {
    console.error("fetchProfileGames:", error);
    return [];
  }
  return data || [];
}
